import React, { useState, useEffect, useCallback, useRef } from "react";
import SubNav from "../../shared/subnav/SubNav";
import CustomerReservationTable from "./CustomerReservationTable";
import { withTranslation } from "react-i18next";
import styles from "./CustomerReservation.module.scss";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import CustomerReservationsClient from "../../shared/clients/CustomerReservationsClient";

function CustomerReservations(props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [tables, setTables] = useState({});
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  const [scrollPos] = useState(() => {
    return props.lastPath &&
    props.lastPath.includes("/customer-reservations/order-details/") &&
    sessionStorage.getItem("scrollPos")
      ? parseInt(sessionStorage.getItem("scrollPos"))
      : 0;
  });

  const fetchFailedRef = useRef(false);
  const tablesCopyRef = useRef({});

  useEffect(() => {
    if (!searchEnabled) {
      tablesCopyRef.current = JSON.parse(JSON.stringify(tables));
    }
  }, [tables, searchEnabled]);

  const updateTables = useCallback((
    reservations,
    numPages,
    numResults,
    commonDealerId,
    newPage
  ) => {
    setTables(prevTables => {
      const tablesCopy = JSON.parse(JSON.stringify(prevTables));
      tablesCopy[commonDealerId] = {
        reservations: reservations,
        numberOfPages: numPages,
        totalNumberOfResults: numResults,
        currentPage: newPage,
      };
      return tablesCopy;
    });
  }, []);

  const parseReservations = useCallback((reservations, numPages, numResults, currentPage) => {
    if (reservations.length === 0) return;

    setTables(prevTables => {
      const currentTables = JSON.parse(JSON.stringify(prevTables));
      const tempReservations = [];
      const dealerId = reservations[0].dealerId;

      reservations.forEach((reservation) => {
        reservation.orderType =
          reservation.orderType === "pre-order"
            ? props.t("CustomerReservations.preOrder")
            : reservation.orderType;

        tempReservations.push(reservation);
      });

      currentTables[dealerId] = {
        reservations: tempReservations,
        numberOfPages: numPages,
        totalNumberOfResults: numResults,
        currentPage: currentPage,
      };

      return currentTables;
    });
  }, [props.t]);

  const getReservations = useCallback(async (dealers) => {
    fetchFailedRef.current = false;
    setFetchFailed(false);

    for (let i = 0; i < dealers.length; i++) {
      const dealerCommonId = dealers[i];
      const currentPage =
        props.lastPath &&
        props.lastPath.includes("/customer-reservations/order-details/") &&
        sessionStorage.getItem("pageNumber_" + dealerCommonId)
          ? parseInt(sessionStorage.getItem("pageNumber_" + dealerCommonId))
          : 1;

      await CustomerReservationsClient.getCustomerReservations(
        dealerCommonId,
        currentPage - 1,
        props.lang
      )
        .then((result) => {
          result.reservations.length &&
          parseReservations(
            result.reservations,
            result.pagination.numberOfPages,
            result.pagination.totalNumberOfResults,
            currentPage
          );
        })
        .catch(() => {
          setFetchFailed(true);
          fetchFailedRef.current = true;
        });
    }
  }, [props.lastPath, props.lang, parseReservations]);

  const fetchReservations = useCallback(() => {
    const commonIds = props.user.getCommonIdsWithSDLR();

    // Check if we have valid dealer IDs before proceeding
    if (!commonIds || commonIds.trim() === "") {
      console.warn(
        "CustomerReservations: No valid dealer common IDs available"
      );
      setIsDataLoaded(true);
      props.hideOrShow(false);
      return;
    }

    const dealers = commonIds.split(",").filter((id) => id && id.trim() !== "");

    if (dealers.length === 0) {
      console.warn("CustomerReservations: No valid dealers after filtering");
      setIsDataLoaded(true);
      props.hideOrShow(false);
      return;
    }

    console.log(
      "[CustomerReservations] fetchReservations commonIds:",
      commonIds
    );
    console.log("[CustomerReservations] fetchReservations dealers:", dealers);

    getReservations(dealers).finally(() => {
      if (!fetchFailedRef.current) {
        setIsDataLoaded(true);
        window.scrollTo(0, scrollPos);
      }
      props.hideOrShow(false);
    });
  }, [props.user, props.hideOrShow, getReservations, scrollPos]);

  const searchOrders = useCallback((e) => {
    e.preventDefault();
    props.hideOrShow(true);
    const dealers = props.user.getCommonIdsForSearch();

    CustomerReservationsClient.searchCustomerReservations(
      searchQuery.trim(),
      props.user,
      props.lang,
      dealers
    )
      .then((reservations) => {

        setTables({});

        if (reservations && reservations.length > 0) {
          const pageSize = 10;
          const numberOfPages = Math.ceil(reservations.length / pageSize);

          parseReservations(
            reservations,
            numberOfPages,
            reservations.length,
            1
          );
        }

        setSearchEnabled(true);
      })
      .catch(() => {
        setTables({});
        setSearchEnabled(true);
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  }, [props.hideOrShow, props.user, props.lang, searchQuery, parseReservations]);

  const clearSearch = useCallback(() => {
    if (searchEnabled) {
      setSearchEnabled(false);
      setTables(tablesCopyRef.current);
      setSearchQuery("");
    }
  }, [searchEnabled]);

  const updateOrder = useCallback(async (
    reservationId,
    dealerActionType,
    dealerCommonId,
    rejectReason
  ) => {
    const dealerActionInfo = {
      reservationId: reservationId,
      dealerAction: dealerActionType,
    };

    if (rejectReason) {
      dealerActionInfo.dealerRejectedReason = rejectReason;
    }

    props.hideOrShow(true);
    CustomerReservationsClient.updateOrder(
      dealerCommonId,
      dealerActionInfo,
      props.user.userId
    )
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        if (searchEnabled) {
          const mockEvent = { preventDefault: () => {} };
          searchOrders(mockEvent);
        } else {
          fetchReservations();
        }

        props.hideOrShow(false);
      });
  }, [props.hideOrShow, props.user.userId, searchEnabled, searchOrders, fetchReservations]);

  useEffect(() => {
    const initialFetch = async () => {
      props.hideOrShow(true, true);

      const commonIds = props.user.getCommonIdsWithSDLR();

      if (!commonIds || commonIds.trim() === "") {
        console.warn("CustomerReservations: No valid dealer common IDs available");
        setIsDataLoaded(true);
        props.hideOrShow(false);
        return;
      }

      const dealers = commonIds.split(",").filter((id) => id && id.trim() !== "");

      if (dealers.length === 0) {
        console.warn("CustomerReservations: No valid dealers after filtering");
        setIsDataLoaded(true);
        props.hideOrShow(false);
        return;
      }

      console.log("[CustomerReservations] initial fetch commonIds:", commonIds);
      console.log("[CustomerReservations] initial fetch dealers:", dealers);

      fetchFailedRef.current = false;
      setFetchFailed(false);

      for (let i = 0; i < dealers.length; i++) {
        const dealerCommonId = dealers[i];
        const currentPage =
          props.lastPath &&
          props.lastPath.includes("/customer-reservations/order-details/") &&
          sessionStorage.getItem("pageNumber_" + dealerCommonId)
            ? parseInt(sessionStorage.getItem("pageNumber_" + dealerCommonId))
            : 1;

        await CustomerReservationsClient.getCustomerReservations(
          dealerCommonId,
          currentPage - 1,
          props.lang
        )
          .then((result) => {
            result.reservations.length &&
            parseReservations(
              result.reservations,
              result.pagination.numberOfPages,
              result.pagination.totalNumberOfResults,
              currentPage
            );
          })
          .catch(() => {
            setFetchFailed(true);
            fetchFailedRef.current = true;
          });
      }

      if (!fetchFailedRef.current) {
        setIsDataLoaded(true);
        window.scrollTo(0, scrollPos);
      }
      props.hideOrShow(false);
    };

    initialFetch();
  }, []);

  useEffect(() => {
    return () => {
      sessionStorage.setItem("scrollPos", window.scrollY);
    };
  }, []);

  const getSubNav = () => {
    return (
      <SubNav
        dashboard={false}
        title={props.t("GlobalNavBar.orders")}
        {...props}
      />
    );
  };

  const searchOrdersComponent = () => {
    return (
      <div className="pagePadding">
        <div className={styles.searchWrapper}>
          <div>{props.t("CustomerReservations.searchBy")}</div>
          <ValidatorForm className={styles.form} onSubmit={searchOrders}>
            <TextValidator
              variant="outlined"
              name="orderSearch"
              value={searchQuery}
              validators={["required"]}
              errorMessages={[props.t("Error.requiredField")]}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className={styles.inputField}
              data-testid="orderSearch"
              placeholder={props.t(
                "CustomerReservations.searchPlaceholder"
              )}
            />

            <button
              className={styles.searchButton}
              type="submit"
              data-testid="searchBtn"
            >
              <div className={styles.searchIcon}></div>
            </button>
          </ValidatorForm>
          <button
            className={styles.clear}
            data-testid="clearSearch"
            onClick={clearSearch}
          >
            {props.t("CustomerReservations.clear")}
          </button>
        </div>
      </div>
    );
  };

  console.log("CustomerReservations state.tables:", tables);
  console.log("CustomerReservations searchEnabled:", searchEnabled);

  return (
    <div className="pageWrapper">
      {getSubNav()}

      {searchOrdersComponent()}

      {isDataLoaded ? (
        Object.keys(tables).length !== 0 ? (
          Object.keys(tables).map((commonDealerId, index) => {
            console.log("Rendering table for dealer:", commonDealerId, tables[commonDealerId]);
            return (
              <CustomerReservationTable
                {...props}
                searchEnabled={searchEnabled}
                key={index}
                fromCrc={false}
                index={index}
                currentPage={tables[commonDealerId].currentPage}
                commonDealerId={commonDealerId}
                reservations={tables[commonDealerId].reservations}
                numberOfPages={tables[commonDealerId].numberOfPages}
                updateTables={updateTables}
                updateOrder={updateOrder}
                totalNumberOfResults={
                  tables[commonDealerId].totalNumberOfResults
                }
              />
            );
          })
        ) : (
          <div
            className="pagePadding"
            data-testid="notFound"
            key="none-found"
          >
            {props.t("CustomerReservations.notFound")}
          </div>
        )
      ) : fetchFailed ? (
        <div className={styles.noDataFoundMsg}>No data found</div>
      ) : null}
    </div>
  );
}

export default withTranslation("emp")(CustomerReservations);
